import { useEffect, useState } from 'react';

import { loadPrism, type UiLayoutApi, type UiLayoutResult } from '../../engine/prism.js';
import { readProjectFile } from '../../engine/host.js';
import { parseWeight, type Box, type StyleSheet, type UiDoc, type Vec2 } from './ui-doc.js';

/**
 * The UI editor's preview uses the engine's real UI layout
 * (`prism_core::ui::layout` through `prism_wasm`'s `UiLayout`), so what the
 * canvas shows is exactly what the game lays out.
 */

let engine: Promise<UiLayoutApi | null> | null = null;

function uiLayoutEngine(): Promise<UiLayoutApi | null> {
  engine ??= loadPrism().then((m) => {
    if (!m?.UiLayout) return null;
    try {
      return new m.UiLayout();
    } catch {
      return null;
    }
  });
  return engine;
}

/** Fonts registered with the engine instance, keyed `projectId:path`. */
const fonts = new Set<string>();
let defaultFontKey = '';

const FONT_RE = /\.(ttf|otf)$/i;

/** Every font file the sheets reference (`fonts` aliases and `font` paths). */
export function sheetFonts(sheets: StyleSheet[]): string[] {
  const out = new Set<string>();
  const visit = (v: unknown) => {
    if (!v || typeof v !== 'object') return;
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      if (k === 'font' && typeof x === 'string' && FONT_RE.test(x)) out.add(x);
      else if (typeof x === 'object') visit(x);
    }
  };
  for (const s of sheets) {
    for (const f of Object.values(s.fonts ?? {})) {
      // A file, or a family of files by weight.
      for (const x of typeof f === 'string' ? [f] : Object.values(f ?? {})) if (typeof x === 'string' && FONT_RE.test(x)) out.add(x);
    }
    visit(s.classes);
  }
  return [...out].sort();
}

export type EngineLayoutState = { state: 'loading' } | { state: 'missing' } | { state: 'ready'; engine: UiLayoutApi; fontsVersion: number };

/** The engine layout instance with the project's fonts loaded. */
export function useUiLayoutEngine(projectId: number | null, fontPaths: string[], defaultFont: string | undefined): EngineLayoutState {
  const [api, setApi] = useState<UiLayoutApi | null | undefined>(undefined);
  const [fontsVersion, setFontsVersion] = useState(0);
  useEffect(() => {
    let cancelled = false;
    void uiLayoutEngine().then((e) => !cancelled && setApi(e));
    return () => {
      cancelled = true;
    };
  }, []);
  const key = fontPaths.join('|');
  useEffect(() => {
    if (!api || !projectId) return;
    let cancelled = false;
    void (async () => {
      let changed = false;
      for (const p of fontPaths) {
        const k = `${projectId}:${p}`;
        if (fonts.has(k)) continue;
        fonts.add(k);
        try {
          changed = api.addFont(p, await readProjectFile(projectId, p)) || changed;
        } catch {
          /* missing font: the validator reports it; text measures with the default font */
        }
      }
      const dk = defaultFont ? `${projectId}:${defaultFont}` : '';
      if (dk !== defaultFontKey) {
        defaultFontKey = dk;
        if (defaultFont) {
          try {
            changed = api.setDefaultFont(await readProjectFile(projectId, defaultFont)) || changed;
          } catch {
            /* missing font */
          }
        }
      }
      if (changed && !cancelled) setFontsVersion((v) => v + 1);
    })();
    return () => {
      cancelled = true;
    };
  }, [api, projectId, key, defaultFont]);
  if (api === undefined) return { state: 'loading' };
  if (api === null) return { state: 'missing' };
  return { state: 'ready', engine: api, fontsVersion };
}

export interface EngineLayout {
  scale: number;
  canvas: Vec2;
  /** Element rects (canvas units, y up) keyed by tree path (`0.1`). */
  boxes: Map<string, Box>;
  error: string | null;
}

/** Lays out `doc` with the engine for a `screen` of pixels. */
export function engineLayout(
  api: UiLayoutApi,
  doc: UiDoc,
  sheets: StyleSheet[],
  settingsUi: unknown,
  screen: Vec2,
): EngineLayout {
  let raw: string;
  try {
    raw = api.layout(JSON.stringify(doc), JSON.stringify(sheets), JSON.stringify(settingsUi ?? {}), screen[0], screen[1]);
  } catch (e) {
    return { scale: 1, canvas: screen, boxes: new Map(), error: e instanceof Error ? e.message : String(e) };
  }
  const v = JSON.parse(raw) as UiLayoutResult | { error: string };
  if ('error' in v) return { scale: 1, canvas: screen, boxes: new Map(), error: v.error };
  const boxes = new Map<string, Box>();
  for (const e of v.elements) boxes.set(e.path, { x: e.rect[0], y: e.rect[1], w: e.rect[2], h: e.rect[3], xform: e.xform ?? undefined });
  return { scale: v.scale, canvas: v.canvas, boxes, error: null };
}

/** Browser font families of project font files (`projectId:path` -> family), for the HTML preview text. */
const cssFamilies = new Map<string, string>();

/**
 * Loads the sheets' font files as CSS FontFaces so the preview draws text in
 * the same fonts the engine lays it out with. Returns a version that changes
 * when a font finished loading (re-render then).
 */
export function useCssFonts(projectId: number | null, fontPaths: string[]): number {
  const [version, setVersion] = useState(0);
  const key = fontPaths.join('|');
  useEffect(() => {
    if (projectId === null || typeof FontFace === 'undefined') return;
    let cancelled = false;
    for (const path of fontPaths) {
      const k = `${projectId}:${path}`;
      if (cssFamilies.has(k)) continue;
      const family = `prism-font-${cssFamilies.size + 1}`;
      cssFamilies.set(k, family);
      void readProjectFile(projectId, path)
        .then((bytes) => new FontFace(family, bytes as BufferSource).load())
        .then((face) => {
          document.fonts.add(face);
          if (!cancelled) setVersion((v) => v + 1);
        })
        .catch(() => cssFamilies.delete(k));
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, key]);
  return version;
}

/** CSS font-family for a style `font` value (an alias of the sheets' `fonts` — a file or a family by weight — or a path). */
export function cssFontFamily(projectId: number | null, font: unknown, sheets: StyleSheet[], fallback?: string, weight = 400): string | undefined {
  let path = typeof font === 'string' ? font : fallback;
  if (path && !FONT_RE.test(path)) {
    for (let i = sheets.length - 1; i >= 0; i--) {
      const alias = sheets[i]!.fonts?.[path];
      if (typeof alias === 'string') {
        path = alias;
        break;
      }
      if (alias && typeof alias === 'object') {
        path = nearestFace(alias, weight) ?? path;
        break;
      }
    }
  }
  if (!path || projectId === null) return undefined;
  const family = cssFamilies.get(`${projectId}:${path}`);
  return family ? `'${family}', system-ui, sans-serif` : undefined;
}

/** The file of a font family (`{ "bold": "fonts/X-Bold.ttf", "500": ... }`) nearest to `weight` (ties: the heavier). */
export function nearestFace(family: Record<string, string>, weight: number): string | undefined {
  let best: { d: number; w: number; path: string } | undefined;
  for (const [k, path] of Object.entries(family)) {
    const w = parseWeight(k);
    if (w === null || typeof path !== 'string') continue;
    const d = Math.abs(w - weight) * 2 - (w > weight ? 1 : 0);
    if (!best || d < best.d || (d === best.d && w > best.w)) best = { d, w, path };
  }
  return best?.path;
}
